/**
 * Single source of truth for "what tenant slug is the user currently
 * inside?" — read by TenantBoundary, useTenantPath, the brand resolver,
 * and every page that needs to namespace API calls or links.
 *
 * Today: the slug is the `:tenantSlug` segment of the matched route.
 *
 * !!! The vanity-domain behavior described below is LIVE IN PRODUCTION on
 * jewishpro.ai. It was forward-ported to this branch from `legacy` commit
 * f4ec4bd4 (the exact source of the running prod build) after the
 * legacy -> main migration dropped it. The implementation lives in
 * VanityRouter.tsx / vanityHost.ts / vanityHistory.ts.
 * See docs/ENVIRONMENTS.md -> "Vanity domains" before editing this file.
 *
 * Vanity behavior: if `window.location.host` resolves to a tenant via
 * `GET /public/brand-from-host`, the slug comes from that host lookup and
 * `source` returns `'host'` — pages drop the slug from their URLs because
 * the host already implies it.
 *
 * Keeping every read funneled through this hook is what keeps vanity
 * domains a one-file change instead of a 26-file rewrite.
 */

import { useParams, useLocation, matchPath } from 'react-router-dom';
import { isReservedSlug } from './reservedSlugs';

export interface ActiveTenant {
  /**
   * Lowercased tenant slug. Empty string when the current URL has no
   * tenant context (e.g. /login, /super-admin, /design-system, or any
   * other reserved root-level path).
   */
  slug: string;
  source: 'path' | 'host';
}

/**
 * In-route hook — uses `useParams`. Returns the slug from the matched
 * `/:tenantSlug/*` route. When called outside a tenant route, returns
 * an empty slug (the caller is responsible for falling back to the
 * authenticated user's `company_tenant_code` if it needs to build a
 * link, via the `buildPath` import from `./paths`).
 */
export function useActiveTenant(): ActiveTenant {
  const params = useParams<{ tenantSlug?: string }>();
  const slug = (params.tenantSlug || '').toLowerCase();
  // Defensive: if React Router ever matched a reserved slug into
  // `:tenantSlug` (it shouldn't, because the reserved routes are
  // declared first), treat as no tenant.
  if (isReservedSlug(slug)) return { slug: '', source: 'path' };
  return { slug, source: 'path' };
}

/**
 * Outside-of-React utility for cases where we have a `location` but no
 * route context (e.g. App.tsx's brand-resolution effect that runs on
 * every `useLocation` change). Uses `matchPath` against the canonical
 * `/:tenantSlug/*` shape.
 */
export function readTenantFromPathname(pathname: string): string {
  const match = matchPath('/:tenantSlug/*', pathname);
  const slug = (match?.params.tenantSlug || '').toLowerCase();
  if (isReservedSlug(slug)) return '';
  return slug;
}

/**
 * Convenience wrapper that pairs `readTenantFromPathname` with
 * `useLocation` for non-route components.
 */
export function useTenantFromLocation(): string {
  const location = useLocation();
  return readTenantFromPathname(location.pathname);
}
